import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, switchMap, map, catchError, withLatestFrom, filter, tap } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { Store } from '@ngrx/store';
import { OrderActions } from './orders.actions';
import { OrderService } from '../../services/order.service';
import { EncryptionService } from '../../zk/encryption.service';
import { decryptInBatches } from '../../zk/batch-decrypt.util';
import { Order } from '../../models/order.model';
import { selectSelectedOrderId } from './orders.reducer';
import { AuditService } from '../../zk/audit.service';

@Injectable()
export class OrdersEffects {
  private actions$ = inject(Actions);
  private svc = inject(OrderService);
  private store = inject(Store);
  private encryption = inject(EncryptionService);
  private audit = inject(AuditService);

  loadOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.loadOrders),
      mergeMap(({ status, partnerId, areaId }) => this.svc.getAll({ status, partnerId, areaId }).pipe(
        mergeMap(orders => from(this.decryptList(orders))),
        map(orders => OrderActions.loadOrdersSuccess({ orders })),
        catchError(err => of(OrderActions.loadOrdersFailure({ error: err.message })))
      ))
    )
  );

  loadOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.loadOrder),
      switchMap(({ id }) => this.svc.getById(id).pipe(
        mergeMap(order => from(this.decryptOne(order))),
        map(order => OrderActions.loadOrderSuccess({ order })),
        catchError(err => of(OrderActions.orderFailure({ error: err.message })))
      ))
    )
  );

  createOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.createOrder),
      mergeMap(({ dto }) => this.svc.create(dto).pipe(
        mergeMap(order => from(this.decryptOne(order))),
        map(order => OrderActions.createOrderSuccess({ order })),
        catchError(err => of(OrderActions.orderFailure({ error: err.message })))
      ))
    )
  );

  createOrderEncrypted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.createOrderEncrypted),
      mergeMap(({ dto }) => this.svc.createEncrypted(dto).pipe(
        mergeMap(order => from(this.decryptOne(order))),
        map(order => OrderActions.createOrderSuccess({ order })),
        catchError(err => of(OrderActions.orderFailure({ error: err.message })))
      ))
    )
  );

  updateOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.updateOrder),
      mergeMap(({ id, dto }) => this.svc.update(id, dto).pipe(
        mergeMap(order => from(this.decryptOne(order))),
        map(order => OrderActions.updateOrderSuccess({ order })),
        catchError(err => of(OrderActions.orderFailure({ error: err.message })))
      ))
    )
  );

  reopenOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.reopenOrder),
      mergeMap(({ id, dto }) => this.svc.reopen(id, dto).pipe(
        mergeMap(order => from(this.decryptOne(order))),
        map(order => OrderActions.reopenOrderSuccess({ order })),
        catchError(err => of(OrderActions.orderFailure({ error: err.message })))
      ))
    )
  );

  addComplaint$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.addComplaint),
      mergeMap(({ orderId, dto }) => this.svc.addComplaint(orderId, dto).pipe(
        map(complaint => OrderActions.addComplaintSuccess({ orderId, complaint })),
        catchError(err => of(OrderActions.orderFailure({ error: err.message })))
      ))
    )
  );

  resolveComplaint$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.resolveComplaint),
      mergeMap(({ orderId, complaintId }) => this.svc.resolveComplaint(orderId, complaintId).pipe(
        map(complaint => OrderActions.resolveComplaintSuccess({ orderId, complaint })),
        catchError(err => of(OrderActions.orderFailure({ error: err.message })))
      ))
    )
  );

  markComplaintWontDo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.markComplaintWontDo),
      mergeMap(({ orderId, complaintId, reason }) => this.svc.markComplaintWontDo(orderId, complaintId, reason).pipe(
        map(complaint => OrderActions.markComplaintWontDoSuccess({ orderId, complaint })),
        catchError(err => of(OrderActions.orderFailure({ error: err.message })))
      ))
    )
  );

  closeOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.closeOrder),
      mergeMap(({ id }) => this.svc.close(id).pipe(
        mergeMap(order => from(this.decryptOne(order))),
        map(order => OrderActions.closeOrderSuccess({ order })),
        catchError(err => of(OrderActions.orderFailure({ error: err.message })))
      ))
    )
  );

  deleteOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.deleteOrder),
      mergeMap(({ id }) => this.svc.delete(id).pipe(
        map(() => OrderActions.deleteOrderSuccess({ id })),
        catchError(err => of(OrderActions.orderFailure({ error: err.message })))
      ))
    )
  );

  // After any complaint mutation, re-fetch the order so the store has authoritative data
  refreshAfterComplaintMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.addComplaintSuccess, OrderActions.resolveComplaintSuccess, OrderActions.markComplaintWontDoSuccess),
      map(({ orderId }) => OrderActions.loadOrder({ id: orderId }))
    )
  );

  // After loadOrders (setAll) wipes per-order data, re-fetch the currently-viewed order
  refreshViewedOrderAfterListLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.loadOrdersSuccess),
      withLatestFrom(this.store.select(selectSelectedOrderId)),
      filter(([, selectedId]) => selectedId != null),
      map(([, selectedId]) => OrderActions.loadOrder({ id: selectedId! }))
    )
  );

  // Phase 8.6 — audit a detail-view decrypt (single-record load only, not the list load).
  auditDecrypt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderActions.loadOrderSuccess),
      tap(({ order }) => this.audit.log('DECRYPT', 'Order', order.id))
    ),
    { dispatch: false }
  );

  // ── Decryption helpers ────────────────────────────────────────────────────

  private async decryptList(orders: Order[]): Promise<Order[]> {
    return decryptInBatches(orders, o => this.decryptOne(o));
  }

  private async decryptOne(order: Order): Promise<Order> {
    if (!order.wrappedDek || !order.encryptedFields || Object.keys(order.encryptedFields).length === 0) {
      return order;
    }
    try {
      const plain = await this.encryption.decryptRecord(order.encryptedFields, order.wrappedDek);
      return { ...order, address: plain['address'] ?? order.address };
    } catch {
      return order;
    }
  }
}
